import { useCallback, useMemo, useSyncExternalStore } from "react";
import { type ChangeTickerBody, metascalpLink } from "./metascalp-link";
import {
  linkStateText,
  metascalpStateText,
  sendToMetascalp,
  sendToNextGroup,
  type TerminalCommand,
  type TerminalNotice,
} from "./terminal-action";
import type { TerminalControls, TerminalSettingsHint } from "./terminal-controls";
import { bindingRotation, groupRotation } from "./terminal-group-rotation";
import { useTerminalSettings } from "./terminal-settings";
import { useTigerLink } from "./use-tiger-link";

/**
 * Собирает то, что экран отдаёт действию «открыть в терминале»: настройки,
 * состояние связи и обработчики. Исход отправки уходит в `onNotice` —
 * экран показывает его тем же сообщением, что и «Скопировано: …». Терминал
 * выбирают настройки (§89 п. 1): при MetaScalp соединения с TigerTrade нет, и
 * команда идёт запросом `change-ticker`.
 */
export function useTerminalSession({
  onNotice,
  onOpenSettings,
}: {
  /** Исход и плотность, по которой действовали: панель чужой плотности его не покажет. */
  onNotice: (notice: TerminalNotice, densityId: string) => void;
  onOpenSettings: (hint?: TerminalSettingsHint) => void;
}): TerminalControls {
  const [settings] = useTerminalSettings();
  const { state, send } = useTigerLink();
  const metascalpState = useSyncExternalStore(
    metascalpLink.subscribe,
    metascalpLink.getState,
    metascalpLink.getState,
  );

  const onSend = useCallback(
    (command: TerminalCommand, densityId: string) => {
      if (command.terminal === "metascalp") {
        const sendBody = (body: ChangeTickerBody) =>
          metascalpLink.send(settings.metascalp.port, body);
        void sendToMetascalp(
          sendBody,
          bindingRotation,
          settings.metascalp.bindings,
          command,
          settings.enabled,
        ).then((notice) => onNotice(notice, densityId));
        return;
      }
      void sendToNextGroup(send, groupRotation, settings.groups, command).then((notice) => {
        if (notice === null) {
          // Набор опустел между отрисовкой кнопки и кликом: вести в настройки.
          onOpenSettings("groups");
          return;
        }
        onNotice(notice, densityId);
      });
    },
    [send, settings.enabled, settings.groups, settings.metascalp, onNotice, onOpenSettings],
  );

  return useMemo(() => {
    if (settings.terminal === "metascalp") {
      // Выключенный терминал называется выключенным, что бы ни помнил клиент о порте.
      const viewState = settings.enabled ? metascalpState : ({ kind: "off" } as const);
      return {
        settings,
        linkState: { terminal: "metascalp", state: viewState.kind },
        statusText: metascalpStateText(viewState, settings.metascalp.port),
        onSend,
        onOpenSettings,
      };
    }
    return {
      settings,
      linkState: { terminal: "tigertrade", state },
      statusText: linkStateText(state),
      onSend,
      onOpenSettings,
    };
  }, [settings, state, metascalpState, onSend, onOpenSettings]);
}
